/**
 * One WebSocket per tab to `ws/notifications/` (JWT as the "bearer" sub-protocol).
 * Reconnects with backoff, keeps itself alive with pings, and fans events out to subscribers.
 *
 *   const off = realtime.on("chat.message", (data) => …);
 *   realtime.send({ type: "chat.typing", conversation: id });
 */
import { refreshAccessToken, tokenStore } from "@/api/client";
import { env } from "@/lib/env";

export interface RealtimeEvent<T = Record<string, unknown>> {
  type: string;
  data: T;
}
type Listener = (data: Record<string, unknown>, event: RealtimeEvent) => void;
type StatusListener = (connected: boolean) => void;

const PING_MS = 25_000;
const MAX_BACKOFF_MS = 30_000;

class RealtimeClient {
  private socket: WebSocket | null = null;
  private listeners = new Map<string, Set<Listener>>();
  private statusListeners = new Set<StatusListener>();
  private wanted = false;
  private attempt = 0;
  private pingTimer: number | undefined;
  private retryTimer: number | undefined;
  connected = false;

  start(): void {
    if (this.wanted || !env.realtimeEnabled) return;
    this.wanted = true;
    void this.open();
  }

  stop(): void {
    this.wanted = false;
    window.clearTimeout(this.retryTimer);
    window.clearInterval(this.pingTimer);
    this.socket?.close(1000, "logout");
    this.socket = null;
    this.setConnected(false);
  }

  on(type: string, listener: Listener): () => void {
    const set = this.listeners.get(type) ?? new Set<Listener>();
    set.add(listener);
    this.listeners.set(type, set);
    return () => set.delete(listener);
  }

  onStatus(listener: StatusListener): () => void {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  send(message: Record<string, unknown>): boolean {
    if (this.socket?.readyState !== WebSocket.OPEN) return false;
    this.socket.send(JSON.stringify(message));
    return true;
  }

  private setConnected(value: boolean): void {
    if (this.connected === value) return;
    this.connected = value;
    this.statusListeners.forEach((l) => l(value));
  }

  private async open(): Promise<void> {
    if (!this.wanted || typeof WebSocket === "undefined") return;
    const token = tokenStore.get() ?? (await refreshAccessToken());
    if (!token || !this.wanted) {
      this.scheduleRetry();
      return;
    }
    const socket = new WebSocket(`${env.wsBaseUrl}/notifications/`, ["bearer", token]);
    this.socket = socket;
    socket.onopen = () => {
      this.attempt = 0;
      this.setConnected(true);
      window.clearInterval(this.pingTimer);
      this.pingTimer = window.setInterval(() => this.send({ type: "ping" }), PING_MS);
    };
    socket.onmessage = (msg) => {
      let event: RealtimeEvent;
      try {
        event = JSON.parse(String(msg.data)) as RealtimeEvent;
      } catch {
        return;
      }
      this.listeners.get(event.type)?.forEach((l) => l(event.data ?? {}, event));
      this.listeners.get("*")?.forEach((l) => l(event.data ?? {}, event));
    };
    socket.onclose = (e) => {
      window.clearInterval(this.pingTimer);
      this.setConnected(false);
      if (this.socket === socket) this.socket = null;
      if (!this.wanted) return;
      // 4401 = expired/invalid token: refresh before trying again.
      if (e.code === 4401) tokenStore.set(null);
      this.scheduleRetry();
    };
  }

  private scheduleRetry(): void {
    if (!this.wanted) return;
    const delay = Math.min(1000 * 2 ** this.attempt, MAX_BACKOFF_MS) * (0.75 + Math.random() * 0.5);
    this.attempt += 1;
    window.clearTimeout(this.retryTimer);
    this.retryTimer = window.setTimeout(() => void this.open(), delay);
  }
}

export const realtime = new RealtimeClient();
